<template>
  <v-container>
    <app-spinner v-if="loading" class="mt-4" />
    <template v-else>
      <TaskExamplesModal
        :modal-visible="showExamplesModal"
        :task-type="taskType"
        :hash-tags="hashtags"
        :mentions="mentions"
        @task-examples-modal-close="showExamplesModal = false"
      />

      <v-row>
        <v-col>
          <LfProofUpload
            :task-type="taskType"
            :business-brand="businessBrand"
            :proof-image="imagePreview"
            :hashtags="hashtags"
            :facebook-mentions="facebookMentions"
            :instagram-mentions="instagramMentions"
            @proof-upload-file-selected="onFileSelected"
            @proof-upload-show-examples-modal="showExamplesModal = true"
          />
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <v-progress-linear v-if="imageUploadProgress >= 0 && imageUploadProgress < 100" v-model="imageUploadProgress" height="25" color="pink" striped>
            <span class="white--text text font-weight-bold">{{ imageProgressAction }}</span>
          </v-progress-linear>
        </v-col>
      </v-row>
      <v-row class="text-center mb-5">
        <v-col>
          <v-btn
            v-if="imagePreview"
            color="primary"
            block
            rounded
            x-large
            :dark="!!imagePreview"
            :loading="shareProofLoading"
            data-cy="shareButton"
            @click="shareProof"
          >
            Submit to Business <v-icon class="pl-1" right dark>mdi-send</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </template>
  </v-container>
</template>

<script>
import Vue from 'vue'
import { mapActions } from 'vuex'

import { ApiService } from '@/services'

import { findByUrlSlug } from '@/enums/taskType'

import imageCompression from 'browser-image-compression'

import { LfProofUpload } from 'lf-common'
import TaskExamplesModal from '@/components/TaskExamplesModal'

export default Vue.extend({
  name: 'ProofPhotoUpload',

  components: {
    LfProofUpload,
    TaskExamplesModal,
  },

  data: () => ({
    loading: true,
    showExamplesModal: false,
    businessBrand: '',
    hashtags: [],
    facebookMentions: [],
    instagramMentions: [],
    imageData: null,
    imagePreview: null,
    imageUploadProgress: -1,
    imageProgressAction: '',
    shareProofLoading: false,
    offer: null,
    voucher: null,
    address: null,
  }),

  computed: {
    taskType: function () {
      return findByUrlSlug(this.$router.currentRoute.params.taskType)
    },
    mentions: function () {
      if (this.taskType.company === 'Facebook') return this.facebookMentions
      if (this.taskType.company === 'Instagram') return this.instagramMentions

      return []
    },
  },

  mounted: function () {
    const voucherId = this.$router.currentRoute.params.voucherId

    ApiService.get(`voucherWorkflow/${voucherId}`).then((response) => {
      if (response.data.error || (response.data.voucher.voucher_status !== 'NEED_PROOF' && response.data.voucher.voucher_status !== 'COMPLETED')) {
        this.$router.replace({ path: '/' })
        this.$toast.warning('Whoops! That offer is no longer available.')
        return
      }

      this.loading = false
      this.businessBrand = response.data.business.brand
      this.offer = response.data.offer
      this.address = response.data.address
      this.voucher = response.data.voucher
      this.hashtags = [...response.data.offer.hashtags, ...(response.data.address?.hashtags || [])]
      this.facebookMentions = [...response.data.offer.facebook_mentions, ...(response.data.address?.facebook_mentions || [])]
      this.instagramMentions = [...response.data.offer.instagram_mentions, ...(response.data.address?.instagram_mentions || [])]
    })
  },

  methods: {
    ...mapActions('reservations', ['fetchReservationSummary']),
    ...mapActions('offerCompletedModal', ['setCompletedOfferModalData']),
    shareProof: function () {
      this.shareProofLoading = true
      this.imageUploadProgress = 0
      this.imageProgressAction = 'Uploading'

      ApiService.get(`signedUrl/proof/${this.imageData.type.split('/')[1]}`)
        .then((response) => {
          return response.data
        })
        .then((signedUrlResponse) => {
          const requestConfig = {
            headers: { 'Content-Type': this.imageData.type },
            onUploadProgress: (progressEvent) => {
              this.imageUploadProgress = (progressEvent.loaded / progressEvent.total) * 100
            },
          }

          return ApiService.customRequest()
            .put(signedUrlResponse.signedUrl, this.imageData, requestConfig)
            .then((response) => {
              return signedUrlResponse.publicUrl
            })
        })
        .then((publicUrl) => {
          const voucherId = this.$router.currentRoute.params.voucherId
          const taskType = findByUrlSlug(this.$router.currentRoute.params.taskType)

          return ApiService.post(`proof`, {
            voucherId,
            imgUrl: publicUrl,
            proofDate: new Date(),
            proofType: taskType.type,
          })
        })
        .then((response) => {
          this.fetchReservationSummary()

          if (this.offer.number_tasks_required === this.voucher.number_tasks_completed + 1) {
            this.setCompletedOfferModalData({
              proofType: this.taskType,
              businessBrand: this.businessBrand,
              offerSummary: this.offer.offer_summary,
              offerShortUrlId: this.offer.short_url_id,
              completedOfferCount: response.data.completedOfferCount,
            })
          }

          this.$router.go(-1)
        })
        .catch(() => {
          this.imageUploadProgress = -1
          this.imageProgressAction = ''

          this.$toast.error('Error Uploading Photo')
        })
    },
    onFileSelected: function (event) {
      if (!event.target.files || !event.target.files[0]) {
        this.$toast.error('Error Uploading Photo')
        return
      }

      this.imageProgressAction = 'Generating Preview'

      imageCompression(event.target.files[0], {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        onProgress: (progress) => {
          this.imageUploadProgress = progress
        },
      })
        .then((response) => {
          this.imageData = response

          imageCompression.getDataUrlFromFile(this.imageData).then((response) => {
            this.imagePreview = response
            this.imageUploadProgress = -1
          })
        })
        .catch(() => {
          this.$toast.error('Error Uploading Photo')
        })
    },
  },
})
</script>
